import { jsx as _jsx } from "react/jsx-runtime";
import { getFilterByIdAndType, isEmptyArray } from '@vista/omnichannel-components-domain';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { useFilterOptions } from '../../hooks/use-filter-options';
import { clearFilter } from '../../utils/filters/clear-filter';
import { allFilterOptionValue } from '../../utils/filters/constants';
import { getNumberOfFiltersApplied } from '../../utils/filters/get-number-of-filters-applied';
import { setFilterValue } from '../../utils/filters/set-filter-value';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { SingleFilterDropdown } from './subcomponents/dropdown';
import { SingleFilterModal } from './subcomponents/modal';
import { SingleFilterSkeleton } from './subcomponents/skeleton';
import { SingleFilterTabs } from './subcomponents/tabs';
import { SingleFilterDisplayMode as DisplayMode } from './types/display-mode';
import { defaultSingleFilterProps, singleFilterPropsValidator } from './types/props';
export const SingleFilter = observer(props => {
    var _a;
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { filterId, displayText, assets, config, cdnOptions } = props;
    const { displayMode, filterType, filter: filterKey, showAllOption } = config;
    const filter = getFilterByIdAndType(filterId, filterType);
    const filtersApplied = filter
        ? getNumberOfFiltersApplied({ id: filterId, type: filterType, keys: [filterKey] }) > 0
        : false;
    const options = useFilterOptions(((_a = filter === null || filter === void 0 ? void 0 : filter.sections[filterKey]) === null || _a === void 0 ? void 0 : _a.options) || [], filterKey, showAllOption);
    const updateFilterProps = useMemo(() => {
        return {
            id: filterId,
            type: filterType,
            key: filterKey,
            multiselect: false,
            selected: true
        };
    }, [filterId, filterType, filterKey]);
    useEffect(() => {
        // If more than 2 filters, there will be an 'all' option so don't need to update the filter to select the first option.
        if (!filter || isEmptyArray(options) || (showAllOption && options.length > 2))
            return;
        if (!filtersApplied) {
            // Select the first filter option
            setFilterValue(Object.assign(Object.assign({}, updateFilterProps), { value: options[0].value }));
        }
    }, [filter, filterId, filterKey, filterType, filtersApplied, options, updateFilterProps, showAllOption]);
    if (!filter || !options.length) {
        return enableSkeletonLoadingIndicators ? _jsx(SingleFilterSkeleton, { displayMode: displayMode }) : null;
    }
    const onClick = (value) => {
        if (value === allFilterOptionValue) {
            clearFilter({ id: filterId, type: filterType, keys: [filterKey] });
        }
        else {
            setFilterValue(Object.assign(Object.assign({}, updateFilterProps), { value }));
        }
    };
    const subComponentProps = {
        filterId,
        config,
        id: `${filterId}_${filterKey}`,
        options,
        displayText,
        displayMode,
        assets,
        onClick,
        cdnOptions,
        filterKey
    };
    const showTabs = displayMode === DisplayMode.PrimaryTab ||
        displayMode === DisplayMode.SecondaryTab ||
        (displayMode === DisplayMode.Auto && options.length <= 6);
    const showDropdown = displayMode === DisplayMode.Dropdown || (displayMode === DisplayMode.Auto && options.length <= 16);
    if (showTabs) {
        return _jsx(SingleFilterTabs, Object.assign({}, subComponentProps));
    }
    if (showDropdown) {
        return _jsx(SingleFilterDropdown, Object.assign({}, subComponentProps));
    }
    return _jsx(SingleFilterModal, Object.assign({}, subComponentProps));
});
SingleFilter.displayName = 'SingleFilter';
/**
 * The **Single Filter** component allows a user to filter a film list or showtime picker based on category, genre, or attribute.
 */
export const SingleFilterComponent = withComponentWrapper(SingleFilter, defaultSingleFilterProps, singleFilterPropsValidator);
